<template>
  <div class="sm:px-4">
    <div class="lg:p-6 rounded relative lg:border border-black-light-2 my-4">
      <template v-if="type === 'booking'">
        <div class="fs-20 font-medium mb-4 mdsm:hidden">
          <supplier-mini-banner :supplier-id="supplierId" />
        </div>
        <hr class="border border-black-light-2 my-6 mdsm:hidden" />
      </template>

      <div>
        <div class="mb-2 flex justify-start items-center">
          <svg width="24" height="24">
            <use xlink:href="/img/sprite.svg#ic_resort"></use>
          </svg>
          <span class="ml-3 font-bold">
            {{ supplierName }}
          </span>
        </div>
        <div class="mb-2 flex justify-start items-center">
          <svg width="24" height="24">
            <use xlink:href="/img/sprite.svg#ic_calendar"></use>
          </svg>
          <span class="ml-3">
            {{ bookingDate }}
          </span>
        </div>
        <div class="mb-2 flex justify-start items-center">
          <svg width="24" height="24">
            <use xlink:href="/img/sprite.svg#ic_clock"></use>
          </svg>
          <span :hidden="!totalDays()" class="ml-3">
            {{ totalDays() + " days / " + totalDays(true) + " nights" }}
          </span>
        </div>
        <div class="flex justify-start items-center">
          <svg width="24" height="24">
            <use xlink:href="/img/sprite.svg#ic_people"></use>
          </svg>
          <span class="ml-3"> {{ totalGuests() }} </span>
        </div>
      </div>
    </div>

    <hr class="border border-black-light-2 my-4 lg:hidden" />

    <div class="lg:p-6 rounded relative lg:border border-black-light-2 mb-4">
      <div v-if="getSelectedRoomDetails.length">
        <div
          v-for="(room, index) in getSelectedRoomDetails"
          :key="index"
          class="mb-6"
        >
          <h5 class="fs-16 font-medium mb-4">{{ room.group }}</h5>

          <div class="grid grid-cols-3 justify-between mb-4">
            <div class="flex flex-col col-span-2">
              <div class="fs-16">
                {{ room.name }}
              </div>
              <div class="fs-14 lg:text-black-light-5">
                with
                {{ room.mealPlanName }}
              </div>
            </div>

            <div class="text-right">
              <currency-context>
                <template v-slot="{ convert }">
                  <span
                    v-text="convert(applyRoomMarkup(room.code, room.price))"
                  ></span>
                </template>
              </currency-context>
            </div>
          </div>
        </div>

        <hr class="border border-black-light-2 my-6" />

        <h4 class="fs-16 font-medium mb-4">Transfer</h4>
        <div
          v-for="transfer in currentTransfers"
          :key="transfer.code"
          class="grid grid-cols-3 justify-between mb-4"
        >
          <div class="flex flex-col col-span-2">
            {{ transfer.qty }} x {{ transfer.text }}
          </div>

          <div class="text-right">
            <span> included </span>
          </div>
        </div>

        <hr class="border border-black-light-2 my-6" />

        <h4 class="fs-20 flex items-center justify-between">
          <div>
            <div class="font-medium">Total</div>

            <div class="fs-14 lg:text-black-light-5 font-normal">
              includes taxes and service fees
            </div>
          </div>
          <span class="font-medium">
            <currency-context>
              <template v-slot="{ convert }">
                <span v-text="convert(getTotalBooking)"></span>
              </template>
            </currency-context>
          </span>
        </h4>

        <hr class="border border-black-light-2 my-6 lg:hidden" />
      </div>
      <div v-else>
        <h4 class="fs-16 font-medium col-span-2 mb-4">Room</h4>
        <div class="grid grid-cols-3 justify-between mb-4">
          <div class="flex flex-col col-span-2">-</div>

          <div class="text-right">-</div>
        </div>

        <hr class="border border-black-light-2 my-6" />

        <h4 class="fs-16 font-medium mb-4">Transfer</h4>
        <div class="grid grid-cols-3 justify-between mb-4">
          <div class="flex flex-col col-span-2">-</div>

          <div class="text-right">-</div>
        </div>

        <hr class="border border-black-light-2 my-6" />

        <h4 class="fs-20 font-medium flex items-center justify-between">
          <span> Total </span>
          <span> - </span>
        </h4>

        <hr class="border border-black-light-2 my-6 lg:hidden" />
      </div>
    </div>

    <slot name="button">
      <button
        @click="$emit('submit')"
        class="flex items-center btn btn-primary justify-center w-full"
      >
        {{ btnName }}
      </button>
    </slot>

    <slot name="warning" />
  </div>
</template>

<script>
import SupplierMiniBanner from "./SupplierMiniBanner";
import CurrencyContext from "../../core/CurrencyContext";
import { mapGetters } from "vuex";
import { fullDisplayDate, totalDays } from "../../../helpers";
import {
  guestsDisplay,
  getTransfers,
} from "../../../helpers/hummingbirdHelpers";

export default {
  components: {
    CurrencyContext,
    SupplierMiniBanner,
  },
  props: [
    "type",
    "supplierInfo",
    "supplierId",
    "supplierName",
    "dates",
    "roomsFilter",
    "btnName",
    "location",
  ],
  computed: {
    ...mapGetters([
      "applyRoomMarkup",
      "getSelectedRoomDetails",
      "getTotalBooking",
    ]),
    bookingDate() {
      const fromDate = this.dates[0];
      const toDate = this.dates[1];

      return fullDisplayDate(fromDate, toDate);
    },
    currentTransfers() {
      return getTransfers(this.getSelectedRoomDetails);
    },
  },
  methods: {
    totalDays(nights = false) {
      const fromDate = this.dates[0];
      const toDate = this.dates[1];

      if (fromDate && toDate) {
        return totalDays(fromDate, toDate, nights);
      } else if (fromDate && !toDate) {
        return totalDays(fromDate, fromDate);
      }

      return "";
    },
    totalGuests() {
      return guestsDisplay(this.roomsFilter);
    },
  },
};
</script>

<style scoped>
.booking-info {
  padding: 1rem;
  background-color: #dae8f4;
  border: 1px solid var(--blue-light-4);
  border-left: 4px solid var(--blue);
  border-radius: 4px;
}

.booking-error {
  padding: 1rem;
  background-color: #feeeed;
  border: 1px solid #e12f22;
  border-left: 4px solid #e12f22;
  border-radius: 4px;
  align-items: start;
  display: flex;
  margin-top: 1rem;
}

@media (max-width: 600px) {
  .booking-info {
    background-color: transparent;
  }
}
</style>
